import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Upload, Trash2, Eye, AlertTriangle, Plus, X, Pencil, ExternalLink } from "lucide-react";
import DriveAccessHelper from "@/components/DriveAccessHelper";

function hasPerm(user, p) {
  return (user?.permissions || []).includes(p);
}

function IssueBadge({ issue }) {
  if (issue.issue === "role_missing_on_mission") {
    return <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-800 rounded-full font-mono">Role absent sur la mission: {issue.role}</span>;
  }
  const parts = [];
  if (issue.diffs?.tjm) parts.push(`TJM PO=${issue.diffs.tjm.po} ≠ mission=${issue.diffs.tjm.mission}`);
  if (issue.diffs?.budget_jh) parts.push(`JH PO=${issue.diffs.budget_jh.po} ≠ mission=${issue.diffs.budget_jh.mission}`);
  return <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-800 rounded-full font-mono">{issue.role}: {parts.join(" · ")}</span>;
}

export default function PurchaseOrders({ missionId, mission, onChange }) {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_purchase_orders");
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewPdfId, setViewPdfId] = useState(null);
  const [editingPoId, setEditingPoId] = useState(null); // null = create mode
  const [form, setForm] = useState({ number: "", start_date: "", roles: [{ name: "", tjm: "", budget_jh: "" }], pdf: null });
  const [driveIssue, setDriveIssue] = useState(null); // {po_id, detail}

  const load = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const r = await api.get(`/missions/${missionId}/purchase-orders`);
      setPos(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(e?.response?.data?.detail || "PO load error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [missionId]);

  if (accessDenied) {
    return <div className="text-sm text-zinc-500 italic" data-testid="po-no-access">Vous n'avez pas la permission de voir les bons de commande de cette mission.</div>;
  }

  const isForfait = mission?.type === "FORFAIT";

  const resetForm = () => setForm({ number: "", start_date: "", roles: [{ name: "", tjm: "", budget_jh: "" }], pdf: null, total_amount_ht: "" });

  const openCreate = () => {
    resetForm();
    setEditingPoId(null);
    setUploadOpen(true);
  };

  const openEdit = (po) => {
    setForm({
      number: po.number,
      start_date: po.start_date,
      roles: po.roles.map((r) => ({ name: r.name, tjm: String(r.tjm ?? 0), budget_jh: String(r.budget_jh ?? 0) })),
      pdf: null, // optional — only replace if user picks a new file
      total_amount_ht: po.total_amount_ht != null ? String(po.total_amount_ht) : "",
    });
    setEditingPoId(po.po_id);
    setUploadOpen(true);
  };

  const addRoleRow = () => setForm((f) => ({ ...f, roles: [...f.roles, { name: "", tjm: "", budget_jh: "" }] }));
  const removeRoleRow = (idx) => setForm((f) => ({ ...f, roles: f.roles.filter((_, i) => i !== idx) }));
  const updateRoleRow = (idx, k, v) => setForm((f) => ({ ...f, roles: f.roles.map((r, i) => i === idx ? { ...r, [k]: v } : r) }));

  const submitUpload = async () => {
    if (!form.number.trim()) return toast.error("PO number required");
    if (!form.start_date) return toast.error("Start date required");
    if (!editingPoId && !form.pdf) return toast.error("PDF requis");
    let cleanedRoles = [];
    if (isForfait) {
      // Iter-191 — FORFAIT POs are billed on the total amount; per-role TJM/JH are not required.
      const tot = parseFloat(form.total_amount_ht);
      if (!tot || tot <= 0) return toast.error("Montant total HT requis pour un BC Forfait");
      cleanedRoles = form.roles.filter((r) => r.name.trim()).map((r) => ({
        name: r.name.trim(),
        tjm: 0,
        budget_jh: 0,
      }));
    } else {
      cleanedRoles = form.roles.filter((r) => r.name.trim()).map((r) => ({
        name: r.name.trim(),
        tjm: parseFloat(r.tjm),
        budget_jh: parseFloat(r.budget_jh),
      }));
      if (cleanedRoles.length === 0) return toast.error("At least one role is required");
      if (cleanedRoles.some((r) => isNaN(r.tjm) || isNaN(r.budget_jh))) return toast.error("TJM and MD must be numeric");
    }
    const fd = new FormData();
    fd.append("number", form.number.trim());
    fd.append("start_date", form.start_date);
    fd.append("roles_json", JSON.stringify(cleanedRoles));
    if (isForfait) fd.append("total_amount_ht", parseFloat(form.total_amount_ht).toString());
    if (form.pdf) fd.append("pdf", form.pdf);
    try {
      let saved;
      if (editingPoId) {
        const r = await api.patch(`/missions/${missionId}/purchase-orders/${editingPoId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        saved = r.data;
        toast.success("Purchase order updated");
      } else {
        const r = await api.post(`/missions/${missionId}/purchase-orders`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        saved = r.data;
        toast.success("Purchase order added");
      }
      setUploadOpen(false);
      setEditingPoId(null);
      resetForm();
      load();
      onChange && onChange();
      // Drive upload may have failed — surface the issue so the user can retry after requesting access.
      const du = saved?.drive_upload;
      if (du && du.status && du.status !== "uploaded" && du.status !== "no_folder") {
        setDriveIssue({ po_id: saved.po_id, detail: du.detail || "Access denied to Google Drive folder", status: du.status });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operation failed");
    }
  };

  const retryDriveUpload = async () => {
    if (!driveIssue?.po_id) return;
    try {
      const r = await api.post(`/missions/${missionId}/purchase-orders/${driveIssue.po_id}/drive-retry`);
      if (r.data?.status === "uploaded") {
        toast.success("Purchase order uploaded to Google Drive");
        setDriveIssue(null);
        load();
      } else {
        setDriveIssue({ po_id: driveIssue.po_id, detail: r.data?.detail || "Still failing — check Drive access", status: r.data?.status });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  const delPO = async (po) => {
    if (!window.confirm(`Delete le PO ${po.number} ?`)) return;
    try {
      await api.delete(`/missions/${missionId}/purchase-orders/${po.po_id}`);
      toast.success("PO deleted");
      load();
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const pdfPath = (poId) => `/missions/${missionId}/purchase-orders/${poId}/pdf`;

  return (
    <div className="space-y-5" data-testid="purchase-orders-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-600">
          {pos.length === 0 ? "No purchase order yet." : `${pos.length} bon(s) de commande`}
        </div>
        {canManage && (
          <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) { resetForm(); setEditingPoId(null); } }}>
            <DialogTrigger asChild>
              <button onClick={openCreate} data-testid="po-upload-open-btn" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
                <Upload size={14} /> Uploader un PO
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="po-upload-dialog">
              <DialogHeader>
                <DialogTitle>{editingPoId ? "Éditer le bon de commande" : "New purchase order"}</DialogTitle>
                <DialogDescription>
                  {editingPoId
                    ? "Modifiez les informations du PO. Laissez le champ PDF vide pour conserver le fichier existant."
                    : "Renseignez les informations contractuelles et joignez le PDF du PO."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>PO number</Label>
                    <Input data-testid="po-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="PO-2026-001" />
                  </div>
                  <div>
                    <Label>Start date</Label>
                    <Input data-testid="po-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>{isForfait ? "Bon de commande Forfait" : "Roles contractuels"}</Label>
                    {!isForfait && (
                      <button type="button" onClick={addRoleRow} data-testid="po-add-role-row" className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1">
                        <Plus size={12} /> Add un rôle
                      </button>
                    )}
                  </div>
                  {isForfait ? (
                    <div className="space-y-2">
                      <Input
                        type="number" min="0" step="0.01"
                        placeholder={`Montant total HT ${mission?.currency || ""}`}
                        value={form.total_amount_ht}
                        onChange={(e) => setForm({ ...form, total_amount_ht: e.target.value })}
                        data-testid="po-total-amount-ht"
                      />
                      <p className="text-[11px] text-zinc-500">
                        Le montant doit correspondre au montant total HT déclaré sur la mission Forfait ({mission?.total_amount_ht ?? "?"} {mission?.currency}).
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {form.roles.map((r, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr_120px_120px_32px] gap-2 items-center" data-testid={`po-role-row-${idx}`}>
                          <Input placeholder="Role name" value={r.name} onChange={(e) => updateRoleRow(idx, "name", e.target.value)} data-testid={`po-role-name-${idx}`} />
                          <Input type="number" step="50" placeholder={`TJM ${mission?.currency || ""}`} value={r.tjm} onChange={(e) => updateRoleRow(idx, "tjm", e.target.value)} data-testid={`po-role-tjm-${idx}`} />
                          <Input type="number" step="0.5" placeholder="JH" value={r.budget_jh} onChange={(e) => updateRoleRow(idx, "budget_jh", e.target.value)} data-testid={`po-role-jh-${idx}`} />
                          {form.roles.length > 1 && (
                            <button type="button" onClick={() => removeRoleRow(idx)} className="p-1 hover:bg-zinc-100 rounded-md text-zinc-400 hover:text-red-600"><X size={14} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>{editingPoId ? "Remplacer le PDF (optionnel)" : "PDF du bon de commande"}</Label>
                  <PdfDropzone
                    file={form.pdf}
                    onFile={(f) => setForm({ ...form, pdf: f })}
                    required={!editingPoId}
                  />
                  <div className="text-[11px] text-zinc-500 mt-1 font-mono">Max 15 MB · format PDF uniquement</div>
                </div>
              </div>
              <DialogFooter>
                <button onClick={() => setUploadOpen(false)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
                <button onClick={submitUpload} data-testid="po-submit-btn" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 inline-flex items-center gap-2">
                  <Upload size={14} /> {editingPoId ? "Save" : "Uploader"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading && <div className="text-sm text-zinc-500 italic">Loading…</div>}

      <div className="space-y-3">
        {pos.map((po) => {
          const total = isForfait
            ? (po.total_amount_ht ?? po.total_ht ?? 0)
            : (po.total_ht || po.roles.reduce((s, r) => s + (r.tjm || 0) * (r.budget_jh || 0), 0));
          const hasMismatch = po.mismatch?.has_mismatch;
          return (
            <div
              key={po.po_id}
              data-testid={`po-card-${po.po_id}`}
              className={`bg-white border rounded-lg p-5 ${hasMismatch ? "border-red-300" : "border-zinc-200"}`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={15} className="text-zinc-500" />
                    <div className="font-heading font-semibold text-base" data-testid={`po-number-${po.po_id}`}>{po.number}</div>
                    {hasMismatch && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-red-100 text-red-800 rounded-full font-mono" data-testid={`po-mismatch-${po.po_id}`}>
                        <AlertTriangle size={10} /> Déphasage
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-mono text-zinc-500">
                    Début {po.start_date}
                    {isForfait
                      ? <> · Total HT {Number(total).toLocaleString()} {mission?.currency || ""}</>
                      : <> · {po.roles.length} rôle(s) · Total HT {Number(total).toLocaleString()} {mission?.currency || ""}</>}
                  </div>
                  {po.drive_upload && (
                    <div className="mt-2 text-[10px] font-mono flex items-center gap-2 flex-wrap">
                      {po.drive_upload.status === "uploaded" && po.drive_upload.web_view_link && (
                        <a href={po.drive_upload.web_view_link} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline inline-flex items-center gap-1" data-testid={`po-drive-link-${po.po_id}`}>
                          <ExternalLink size={10} /> Ouvert dans Drive
                        </a>
                      )}
                      {po.drive_upload.status === "no_folder" && (
                        <span className="text-zinc-500">No Drive folder configured on this mission</span>
                      )}
                      {(po.drive_upload.status === "access_denied" || po.drive_upload.status === "error") && canManage && (
                        <>
                          <span className="text-red-700 inline-flex items-center gap-1"><AlertTriangle size={10} /> Drive upload failed</span>
                          <button
                            onClick={() => setDriveIssue({ po_id: po.po_id, detail: po.drive_upload.detail, status: po.drive_upload.status })}
                            data-testid={`po-drive-retry-${po.po_id}`}
                            className="text-blue-700 hover:underline"
                          >
                            Retry
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {po.local_pdf_purged_at && (po.drive_view_url || po.drive_upload?.web_view_link) ? (
                    <a
                      href={po.drive_view_url || po.drive_upload?.web_view_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`po-view-drive-${po.po_id}`}
                      className="p-2 hover:bg-zinc-100 rounded-md text-zinc-600"
                      title="Open PDF on Google Drive (local copy purged)"
                    >
                      <Eye size={15} />
                    </a>
                  ) : (
                    <button onClick={() => setViewPdfId(po.po_id)} data-testid={`po-view-${po.po_id}`} className="p-2 hover:bg-zinc-100 rounded-md text-zinc-600" title="View PDF">
                      <Eye size={15} />
                    </button>
                  )}
                  {canManage && (
                    <button onClick={() => openEdit(po)} data-testid={`po-edit-${po.po_id}`} className="p-2 hover:bg-zinc-100 rounded-md text-zinc-600 hover:text-zinc-900" title="Éditer le PO">
                      <Pencil size={15} />
                    </button>
                  )}
                  {canManage && (
                    <button onClick={() => delPO(po)} data-testid={`po-delete-${po.po_id}`} className="p-2 hover:bg-zinc-100 rounded-md text-zinc-500 hover:text-red-600" title="Delete">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t border-zinc-100 pt-3">
                {isForfait ? (
                  <div className="text-sm text-zinc-700" data-testid={`po-forfait-summary-${po.po_id}`}>
                    Bon de commande forfaitaire — montant unique. Pas de ventilation par rôle.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-[1fr_120px_120px] gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
                      <div>Role</div>
                      <div>TJM</div>
                      <div>JH</div>
                    </div>
                    {po.roles.map((r, i) => (
                      <div key={i} className="grid grid-cols-[1fr_120px_120px] gap-2 text-sm py-1">
                        <div>{r.name}</div>
                        <div className="font-mono">{r.tjm}</div>
                        <div className="font-mono">{r.budget_jh}</div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {hasMismatch && (
                <div className="mt-3 border-t border-red-100 pt-3 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-mono">Mismatches detected</div>
                  <div className="flex flex-wrap gap-2">
                    {po.mismatch.issues.map((iss, i) => <IssueBadge key={i} issue={iss} />)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <PDFViewerDialog
        open={!!viewPdfId}
        onClose={() => setViewPdfId(null)}
        path={viewPdfId ? pdfPath(viewPdfId) : null}
      />

      <Dialog open={!!driveIssue} onOpenChange={(o) => !o && setDriveIssue(null)}>
        <DialogContent data-testid="po-drive-retry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={16} /> Drive upload failed — access needed
            </DialogTitle>
            <DialogDescription>
              The purchase order PDF could not be uploaded to the shared Drive folder because the BAIM account does not have write access to it. Request access from the folder owner using the helper below, then retry once granted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {driveIssue?.detail && (
              <div className="text-xs font-mono text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-2 py-1 break-all">{driveIssue.detail}</div>
            )}
            <DriveAccessHelper
              folderUrl={mission?.drive_folder_url}
              missionTitle={mission?.title}
              kind="purchase order"
              testid="po-drive"
            />
            <div className="text-xs text-zinc-600">Once access is granted, click <strong>Retry</strong> below to re-upload.</div>
          </div>
          <DialogFooter>
            <button onClick={() => setDriveIssue(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Later</button>
            <button onClick={retryDriveUpload} data-testid="po-drive-retry-confirm" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 inline-flex items-center gap-2">
              <Upload size={13} /> Retry
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PDFViewerDialog({ open, onClose, path }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let revokeMe = null;
    const run = async () => {
      if (!open || !path) { setBlobUrl(null); return; }
      try {
        const resp = await api.get(path, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        revokeMe = url;
        setBlobUrl(url);
      } catch (e) {
        toast.error("Unable to load the PDF");
        setBlobUrl(null);
      }
    };
    run();
    return () => { if (revokeMe) URL.revokeObjectURL(revokeMe); };
  }, [open, path]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="po-pdf-dialog">
        <DialogHeader>
          <DialogTitle>Purchase order — visualisation</DialogTitle>
          <DialogDescription>Consultation en lecture seule.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 bg-zinc-100 rounded-md overflow-hidden">
          {blobUrl ? (
            <iframe src={blobUrl} className="w-full h-full border-0" title="Purchase order PDF" data-testid="po-pdf-iframe" />
          ) : (
            <div className="flex items-center justify-center h-full text-zinc-500 text-sm">Loading…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PdfDropzone({ file, onFile, required }) {
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef(null);

  const pick = (f) => {
    if (!f) return onFile(null);
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("File must be a PDF");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 15 MB)");
      return;
    }
    onFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) pick(f);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
      onDrop={onDrop}
      data-testid="po-pdf-dropzone"
      className={`flex items-center justify-center gap-3 px-4 py-8 border-2 border-dashed rounded-md cursor-pointer transition-all
        ${dragOver ? "border-zinc-900 bg-zinc-50" : file ? "border-green-400 bg-green-50/50" : "border-zinc-300 hover:border-zinc-500 hover:bg-zinc-50"}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        data-testid="po-pdf-file"
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0] || null)}
      />
      {file ? (
        <>
          <FileText size={18} className="text-green-700" />
          <div className="text-sm">
            <div className="font-medium" data-testid="po-pdf-selected-name">{file.name}</div>
            <div className="text-xs font-mono text-zinc-500">{(file.size / 1024).toFixed(0)} KB</div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFile(null); }}
            className="ml-2 p-1.5 text-zinc-500 hover:text-red-600 hover:bg-white rounded"
            data-testid="po-pdf-clear"
          >
            <X size={14} />
          </button>
        </>
      ) : (
        <>
          <Upload size={20} className="text-zinc-500" />
          <div className="text-sm text-zinc-700">
            <strong>Drag and drop</strong> le PDF ici, ou <span className="underline">parcourir</span>
            {required && <span className="text-red-600 ml-1">*</span>}
          </div>
        </>
      )}
    </div>
  );
}
